import { Modal, message, Spin, Result } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { findModel } from "../../templates/main-template/MainTamplate.utils";
import { useQuery } from "@apollo/client/react";
import { useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { ModalForm } from "../../molecules";
import { SingleOrderContextProvider } from "../single-order-context/SingleOrderContextProvider";
import { GET_ITEM_MODEL } from "../../../graphql/items";
import { MainTemplateLoading } from "../../templates/main-template/MainTemplate.styled";
import { createShema, joiResolver } from "../single-order/SingleOrder.utils";
import { useQueryClient } from "react-query";
import { newIdempotencyKey } from "../../../utils/idempotency";
import { apolloClient } from "../../../apolloClient";
import { GET_PAYMENTS } from "../../../graphql/payroll";

export const AddOrderModal: React.FC<{
  open: any;
  setOpen: any;
  isLoading: any;
  mutateAsync: any;
  hardcodedControls: any;
}> = ({ open, setOpen, mutateAsync, isLoading, hardcodedControls }) => {
  const { name } = useParams();
  const queryClient = useQueryClient();
  const models: any = queryClient.getQueryData(["/models"]);
  const { model, error: modelError } = useMemo(() => findModel(models, name), [
    models,
    name,
  ]);

  const { data, error, loading: isFetching } = useQuery(GET_ITEM_MODEL, {
    variables: { id: Number(model?.id) },
    skip: !model || !open,
  });

  const isError = !!error;
  const modelData = (data as any)?.itemModel?.model;
  const schema = modelData && createShema([...hardcodedControls, ...modelData]);
  const methods = useForm({
    resolver: schema && joiResolver(schema),
  });

  const isSubmittingRef = useRef(false);
  const idempotencyKeyRef = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open && !idempotencyKeyRef.current) {
      idempotencyKeyRef.current = newIdempotencyKey();
    }
  }, [open ]);

  const submit = () => {
    if (isSubmittingRef.current) return;
    methods.handleSubmit(async (data) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setIsSubmitting(true);
      const id = Number(model.id);
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = newIdempotencyKey();
      }
      try {
        await mutateAsync({ id, body: data, idempotencyKey: idempotencyKeyRef.current });
        idempotencyKeyRef.current = null;
        methods.reset();
        setOpen(false);
      } catch (error: any) {
        const errorText = [
          error?.message,
          ...(error?.graphQLErrors ?? []).map((e: any) => e?.message),
        ]
          .filter(Boolean)
          .join(" ");
        const isPaymentConflict =
          errorText.includes("different payload") ||
          errorText.includes("already exists for this idempotency key");
        if (isPaymentConflict) {
          idempotencyKeyRef.current = null;
          methods.reset();
          setOpen(false);
          message.warning(
            "Wpłata o tym samym kluczu, ale innych danych, już istnieje. Lista została odświeżona."
          );
          try {
            await apolloClient.refetchQueries({ include: [GET_PAYMENTS] });
          } catch (refetchError) {
            console.error(refetchError);
          }
        } else {
          // Keep modal, form data and idempotency key for safe retry.
          console.error(error);
        }
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    }, console.error)();
  };

  const canel = () => {
    idempotencyKeyRef.current = null;
    methods.reset();
    setOpen(false);
  };

  return (
    <Modal
      maskClosable={false}
      confirmLoading={isLoading}
      title="Dodaj nowy rekord"
      centered
      open={open}
      okButtonProps={{
        disabled: isError || isFetching || isSubmitting,
        loading: isFetching,
      }}
      onOk={submit}
      onCancel={canel}
      width={1000}
    >
      <>
        {isFetching && (
          <MainTemplateLoading>
            <Spin size="large" />
          </MainTemplateLoading>
        )}

        {data && !modelError && (
          <SingleOrderContextProvider disableReadMode={true}>
            <FormProvider {...methods}>
              <ModalForm
                controls={modelData}
                hardcodedControls={hardcodedControls}
              />
            </FormProvider>
          </SingleOrderContextProvider>
        )}

        {(isError || modelError) && (
          <Result
            status="403"
            title="BŁĄD"
            subTitle="Przepraszamy, wystąpił błąd. Skontaktuj się z administratorem"
          />
        )}
      </>
    </Modal>
  );
};
